import * as StoreReview from 'expo-store-review';
import React from 'react';
import {
  Dimensions,
  findNodeHandle,
  Platform,
  Pressable,
  View,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useReservation } from '@/api/reservations/use-reservation';
import { useCreateReview } from '@/api/reviews/use-create-review';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Card, showErrorMessage, Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { InputMulti } from '@/ui/input-multi';

const CREDIT_AMOUNT = 25;
const OVERALL_LABELS = [
  'Poor',
  'Fair',
  'Good',
  'Very Good',
  'Excellent',
] as const;
const STAR_PATH =
  'M12 0L14.6942 8.2918H23.4127L16.3593 13.4164L19.0534 21.7082L12 16.5836L4.94658 21.7082L7.64074 13.4164L0.587322 8.2918H9.30583L12 0Z';

const SUB_RATING_QUESTIONS = [
  { key: 'carCondition', label: 'Car Condition' },
  { key: 'cleanliness', label: 'Cleanliness' },
  { key: 'pickupReturn', label: 'Pickup & Return' },
  { key: 'appExperience', label: 'App & Experience' },
  { key: 'customerSupport', label: 'Customer Support' },
] as const;

const ACCESS_METHODS = ['Physical Keycard', 'ChargeKey App', 'Other'] as const;
const CONTACTED_PARTIES = ['Eon Customer Support', 'Owner', 'No'] as const;
const ISSUE_CONTACTS = ['Eon', 'Owner'] as const;
const SUPPORT_CHANNELS = ['Email', 'Phone'] as const;
const YES_NO = ['No', 'Yes'] as const;

type SubRatingKey = (typeof SUB_RATING_QUESTIONS)[number]['key'];
type SubRatings = Record<SubRatingKey, number | null>;

const EMPTY_SUB_RATINGS: SubRatings = {
  carCondition: null,
  cleanliness: null,
  pickupReturn: null,
  appExperience: null,
  customerSupport: null,
};

type ReviewScrollable = {
  scrollTo?: (options: { y: number; animated?: boolean }) => void;
  scrollToPosition?: (x: number, y: number, animated?: boolean) => void;
  getInnerViewNode?: () => number | null;
  getScrollResponder?: () => unknown;
};

interface ReviewProps {
  id: number;
  scrollViewRef?: React.RefObject<ReviewScrollable | null>;
}

export function Review({ id, scrollViewRef }: ReviewProps) {
  const { styles } = useStyles(stylesheet);
  const [overallRating, setOverallRating] = React.useState<number | null>(null);
  const [subRatings, setSubRatings] =
    React.useState<SubRatings>(EMPTY_SUB_RATINGS);
  const [accessMethod, setAccessMethod] = React.useState<string | null>(null);
  const [contactedParty, setContactedParty] = React.useState<string | null>(
    null
  );
  const [hadIssue, setHadIssue] = React.useState<string | null>(null);
  const [issueContact, setIssueContact] = React.useState<string | null>(null);
  const [supportChannel, setSupportChannel] = React.useState<string | null>(
    null
  );
  const [supportResolved, setSupportResolved] = React.useState<string | null>(
    null
  );
  const [detailText, setDetailText] = React.useState('');
  const [hasSubmittedDetails, setHasSubmittedDetails] = React.useState(false);
  const cardRef = React.useRef<View>(null);
  const pendingScrollIntoView = React.useRef(false);

  const { data: resData } = useReservation({
    variables: { id },
    enabled: !!id,
  });

  const { mutateAsync: createReview, isPending } = useCreateReview();

  const existingReview = resData?.review ?? null;
  const hadReviewOnLoad = React.useRef<boolean | null>(null);
  if (resData && hadReviewOnLoad.current === null) {
    hadReviewOnLoad.current = !!existingReview;
  }
  const firstName = resData?.customer.firstName ?? resData?.customer.firstname;
  const carId = resData?.car.id != null ? String(resData.car.id) : null;
  const ownerId = resData?.ownerId != null ? String(resData.ownerId) : null;
  const isOfflineCar = !!resData?.car.isOffline;
  const accessOptions = isOfflineCar
    ? ACCESS_METHODS.filter((method) => method !== 'ChargeKey App')
    : ACCESS_METHODS;

  const submittedRating = overallRating ?? existingReview?.score ?? null;
  const isSubmitted = hasSubmittedDetails || hadReviewOnLoad.current === true;
  const showForm = overallRating != null && !isSubmitted;

  const handleOverallRating = async (rating: number) => {
    if (rating === overallRating) return;

    const isFirstRating = overallRating == null;

    mixpanelService.track('Trip Reviewed', {
      Rating: rating,
      'Is Update': !isFirstRating,
      'Car ID': carId,
      'Owner ID': ownerId,
    });
    setOverallRating(rating);

    if (isSubmitted || rating < 4 || Platform.OS === 'web') return;
    if (!(await StoreReview.hasAction())) return;

    mixpanelService.track('App Store Review Requested', {
      Outcome: 'Shown',
      Rating: rating,
    });
    StoreReview.requestReview();
  };

  const handleHadIssue = (value: string) => {
    setHadIssue(value);
    if (value !== 'Yes') {
      setIssueContact(null);
      setSupportChannel(null);
      setSupportResolved(null);
    }
  };

  const hasOptionalDetails = () => {
    const hasSubRating = SUB_RATING_QUESTIONS.some(
      (question) => subRatings[question.key] != null
    );
    return (
      hasSubRating ||
      accessMethod != null ||
      contactedParty != null ||
      hadIssue != null ||
      issueContact != null ||
      supportChannel != null ||
      supportResolved != null ||
      detailText.trim().length > 0
    );
  };

  const handleSubmitDetails = async () => {
    if (overallRating == null || isPending) return;

    try {
      const issueBranch = hadIssue === 'Yes';
      await createReview({
        score: overallRating,
        reservationId: Number(id),
        feedback: detailText,
        carConditionRating: subRatings.carCondition,
        cleanlinessRating: subRatings.cleanliness,
        pickupReturnRating: subRatings.pickupReturn,
        appExperienceRating: subRatings.appExperience,
        customerSupportRating: subRatings.customerSupport,
        accessMethod,
        contactedParty,
        hadIssue,
        issueContact: issueBranch ? issueContact : null,
        supportChannel: issueBranch ? supportChannel : null,
        supportResolved: issueBranch ? supportResolved : null,
      });
      pendingScrollIntoView.current = true;
      setHasSubmittedDetails(true);

      if (hasOptionalDetails()) {
        mixpanelService.track('Trip Review Details Added', {
          Rating: overallRating,
          'Car Condition Rating': subRatings.carCondition,
          'Cleanliness Rating': subRatings.cleanliness,
          'Pickup & Return Rating': subRatings.pickupReturn,
          'App & Experience': subRatings.appExperience,
          'Customer Support': subRatings.customerSupport,
          'Access Method': accessMethod,
          'Contacted Party': contactedParty,
          'Had Issue': hadIssue,
          'Issue Contact': issueBranch ? issueContact : null,
          'Support Channel': issueBranch ? supportChannel : null,
          'Support Resolved': issueBranch ? supportResolved : null,
          'Detail Text': detailText.trim() ? detailText : null,
          'Car ID': carId,
          'Owner ID': ownerId,
        });
      }

      mixpanelService.flush();
    } catch (e) {
      showErrorMessage((e as Error).message);
    }
  };

  // Submit collapses the form; wait for that layout, then keep the card on
  // screen. Web has scrollIntoView. RN does not, so we measure the card
  // against the parent ScrollView and scrollTo a centered offset.
  const handleCardLayout = () => {
    if (!pendingScrollIntoView.current) return;
    pendingScrollIntoView.current = false;

    requestAnimationFrame(() => {
      const card = cardRef.current;
      if (!card) return;

      if (Platform.OS === 'web') {
        (
          card as View & { scrollIntoView?: (options: object) => void }
        ).scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        return;
      }

      const scrollView = scrollViewRef?.current;
      const responder =
        typeof scrollView?.getScrollResponder === 'function'
          ? (scrollView.getScrollResponder() as ReviewScrollable)
          : scrollView;
      const inner =
        responder?.getInnerViewNode?.() ?? findNodeHandle(responder as never);
      if (!responder || inner == null) return;

      card.measureLayout(
        inner as never,
        (...layout) => {
          const y = layout[1];
          const height = layout[3];
          const targetY = Math.max(
            0,
            y - Math.max(0, (Dimensions.get('window').height - height) / 2)
          );
          if (typeof responder.scrollTo === 'function') {
            responder.scrollTo({ y: targetY, animated: true });
          } else {
            responder.scrollToPosition?.(0, targetY, true);
          }
        },
        () => {}
      );
    });
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <Text type="title2">Rate Your Trip</Text>
      <Text type="bodyRegular" style={styles.headerSubtitle}>
        {`Let us know how your trip went and earn $${CREDIT_AMOUNT} in Eon credits for your next one!`}
      </Text>
    </View>
  );

  const renderSuccess = () => {
    const isPositive = submittedRating != null && submittedRating >= 4;
    const thanks = firstName ? `Thanks, ${firstName}!` : 'Thanks!';

    return (
      <View style={styles.successBody}>
        <View style={styles.successCopy}>
          <Text type="title2" style={styles.centeredText}>
            {thanks}
          </Text>
          <Text type="bodyRegular" style={styles.successSubtitle}>
            {isPositive
              ? 'Your rating helps owners keep their cars in great shape.'
              : "We're sorry this trip missed the mark. Our support team will review your experience."}
          </Text>
          <Chip
            type={ChipType.SUCCESS}
            text={`$${CREDIT_AMOUNT} in Eon credit added`}
            style={styles.creditChip}
            textStyle={styles.creditChipText}
          />
        </View>
      </View>
    );
  };

  const renderOverallStars = () => (
    <View style={styles.overallBlock}>
      <Text type="bodyRegular" style={styles.centeredText}>
        How was your trip overall?
      </Text>
      <StarRow
        value={overallRating}
        onChange={handleOverallRating}
        size="overall"
      />
      {overallRating != null ? (
        <Text type="bodyRegular" style={styles.centeredText}>
          {OVERALL_LABELS[overallRating - 1]}
        </Text>
      ) : null}
    </View>
  );

  const renderForm = () => (
    <View style={styles.formBody}>
      {renderOverallStars()}

      <View style={styles.divider} />

      <View style={styles.subRatings}>
        {SUB_RATING_QUESTIONS.map((question, index) => (
          <React.Fragment key={question.key}>
            {index > 0 ? <View style={styles.rowDivider} /> : null}
            <View style={styles.subRatingRow}>
              <Text type="bodyRegular">{question.label}</Text>
              <StarRow
                value={subRatings[question.key]}
                onChange={(rating) =>
                  setSubRatings((current) => ({
                    ...current,
                    [question.key]: rating,
                  }))
                }
                size="category"
              />
            </View>
          </React.Fragment>
        ))}
      </View>

      <View style={styles.divider} />

      <View style={styles.questions}>
        <QuestionGroup label="Car access" prompt="How did you access the car?">
          <OptionPills
            options={accessOptions}
            value={accessMethod}
            onChange={setAccessMethod}
          />
        </QuestionGroup>

        <View style={styles.rowDivider} />

        <QuestionGroup
          label="Communication"
          prompt="Did you have to contact any of these?"
        >
          <OptionPills
            options={CONTACTED_PARTIES}
            value={contactedParty}
            onChange={setContactedParty}
          />
        </QuestionGroup>

        <View style={styles.rowDivider} />

        <View style={styles.supportGroup}>
          <QuestionGroup
            label="Customer support"
            prompt="Did you have an issue during this trip?"
          >
            <OptionPills
              options={YES_NO}
              value={hadIssue}
              onChange={handleHadIssue}
            />
          </QuestionGroup>

          {hadIssue === 'Yes' ? (
            <>
              <QuestionGroup prompt="Who did you contact about it?">
                <OptionPills
                  options={ISSUE_CONTACTS}
                  value={issueContact}
                  onChange={setIssueContact}
                />
              </QuestionGroup>
              <QuestionGroup prompt="How did you reach them?">
                <OptionPills
                  options={SUPPORT_CHANNELS}
                  value={supportChannel}
                  onChange={setSupportChannel}
                />
              </QuestionGroup>
              <QuestionGroup prompt="Did they resolve it?">
                <OptionPills
                  options={YES_NO}
                  value={supportResolved}
                  onChange={setSupportResolved}
                />
              </QuestionGroup>
            </>
          ) : null}
        </View>
      </View>

      <InputMulti
        label={
          hadIssue === 'Yes'
            ? 'Describe what happened, so we can improve'
            : 'Any additional details?'
        }
        text={detailText}
        onChangeText={setDetailText}
        placeholder="Add more detail (optional)"
        style={styles.detailInput}
      />

      <Button
        type="primary"
        text="Submit & Claim Eon Credit"
        loading={isPending}
        onPress={handleSubmitDetails}
      />
    </View>
  );

  return (
    <View ref={cardRef} collapsable={false} onLayout={handleCardLayout}>
      <Card>
        {renderHeader()}
        {isSubmitted ? (
          renderSuccess()
        ) : showForm ? (
          renderForm()
        ) : (
          <View style={styles.collapsedBody}>{renderOverallStars()}</View>
        )}
      </Card>
    </View>
  );
}

export default Review;

function RatingStar({ filled, size }: { filled: boolean; size: number }) {
  const { theme } = useStyles();
  const height = size * (22 / 24);

  return (
    <Svg width={size} height={height} viewBox="0 0 24 22">
      <Path
        d={STAR_PATH}
        fill={filled ? theme.colors.icon.brandColor : 'none'}
        stroke={filled ? theme.colors.icon.brandColor : theme.colors.icon.color}
        strokeWidth={filled ? 0 : 1.4}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function StarRow({
  value,
  onChange,
  size,
}: {
  value: number | null;
  onChange: (rating: number) => void;
  size: 'overall' | 'category';
}) {
  const { styles } = useStyles(stylesheet);
  const isOverall = size === 'overall';
  const starSize = isOverall ? 34 : 19;

  return (
    <View style={isOverall ? styles.overallStars : styles.categoryStars}>
      {[1, 2, 3, 4, 5].map((rating) => (
        <Pressable
          key={rating}
          onPress={() => onChange(rating)}
          style={isOverall ? styles.overallStarHit : styles.categoryStarHit}
          hitSlop={4}
        >
          <RatingStar
            filled={value != null && rating <= value}
            size={starSize}
          />
        </Pressable>
      ))}
    </View>
  );
}

function OptionPills({
  options,
  value,
  onChange,
}: {
  options: readonly string[];
  value: string | null;
  onChange: (value: string) => void;
}) {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.pills}>
      {options.map((option) => (
        <Button
          key={option}
          type={value === option ? 'primarySmall' : 'secondarySmall'}
          text={option}
          onPress={() => onChange(option)}
          style={styles.pill}
        />
      ))}
    </View>
  );
}

function QuestionGroup({
  label,
  prompt,
  children,
}: {
  label?: string;
  prompt: string;
  children: React.ReactNode;
}) {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.questionGroup}>
      {label ? (
        <Text type="caption1" style={styles.sectionLabel}>
          {label}
        </Text>
      ) : null}
      <View style={styles.question}>
        <Text type="bodyRegular">{prompt}</Text>
        {children}
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  header: {
    padding: 16,
    gap: 4,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  headerSubtitle: {
    color: theme.colors.text.secondary,
    maxWidth: 300,
  },
  collapsedBody: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  formBody: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 16,
    gap: 24,
  },
  overallBlock: {
    alignItems: 'center',
    gap: 24,
  },
  overallStars: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 24,
    width: '100%',
  },
  overallStarHit: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryStars: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  categoryStarHit: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  centeredText: {
    textAlign: 'center',
    width: '100%',
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.card.default.borderColor,
    marginHorizontal: -16,
  },
  rowDivider: {
    height: 1,
    backgroundColor: theme.colors.card.default.borderColor,
  },
  subRatings: {
    gap: 8,
  },
  subRatingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  questions: {
    gap: 16,
    width: '100%',
  },
  questionGroup: {
    gap: 16,
    width: '100%',
  },
  supportGroup: {
    gap: 16,
    width: '100%',
  },
  sectionLabel: {
    color: theme.colors.text.secondary,
  },
  question: {
    gap: 8,
    width: '100%',
  },
  pills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  pill: {
    width: 'auto',
  },
  detailInput: {
    minHeight: 88,
  },
  successBody: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    alignItems: 'center',
  },
  successCopy: {
    gap: 8,
    alignItems: 'center',
    width: '100%',
  },
  successSubtitle: {
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
  creditChip: {
    alignSelf: 'center',
    height: 32,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  creditChipText: {
    fontSize: 12,
    lineHeight: 16,
  },
}));
